/**
 * PluUploadModal — fallback when the GPU doesn't publish a commune's PLU.
 *
 * Two-step wizard :
 *   1. Upload — l'user dépose le règlement écrit + le plan graphique en PDF
 *      (Firebase Storage owner-only, < 80 MB chacun, contentType: PDF). Les
 *      deux fichiers sont stockés à un chemin fixe par fiche, donc un nouvel
 *      upload du même type remplace le précédent.
 *   2. Confirm — Claude a tenté de localiser la parcelle sur le plan graphique
 *      (passe Vision). On affiche le PDF du plan dans un iframe + un select
 *      pré-rempli avec la prédiction. L'user **doit** confirmer ou changer
 *      avant de relancer l'extraction. Un humain reste dans la boucle —
 *      indispensable pour un truc juridiquement engageant.
 *
 * À la confirmation, on appelle `onZoneConfirmed` ; le parent re-consomme un
 * crédit puis lance `extractReglementFromUserUpload` (cf. pipeline.ts).
 *
 * Garde-fous :
 *   - Storage rules : PDF only, ≤ 80 MB, owner-only.
 *   - Backend : max 5 validations par fiche (Firestore counter).
 *   - Bail-out gratuit côté serveur si !isPlu ou commune mismatch (pas de
 *     passe Vision déclenchée → coût plafond ~0,02 €/refus).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../lib/firebase";
import {
  refinePluZoneFromPin,
  validatePluUpload,
  type RefinedPluZone,
} from "../lib/pipeline";
import type { CadastreParcel, PluUploadValidation, PluUploadZone } from "../lib/types";
import { Button, Card, Pill } from "./ui";
import { Ic } from "./icons";
import { SatelliteMap } from "./SatelliteMap";
import { PdfPagePinViewer, type PinPayload } from "./PdfPagePinViewer";

// File caps mirror storage.rules — caught client-side first for a snappier UX.
const MAX_PDF_BYTES = 80 * 1024 * 1024;

type Props = {
  open: boolean;
  uid: string;
  ficheId: string;
  expectedInsee: string;
  expectedCommuneName: string;
  lat: number;
  lng: number;
  // The user's parcel — used for the ortho mini-map in the confirm step
  // (visual matching cadastre ↔ plan graphique) and as the geometry source
  // sent to the backend Vision pass.
  parcel: CadastreParcel;
  onClose: () => void;
  // Fired after the user confirmed a zone — parent consumes a credit and
  // calls extractReglementFromUserUpload.
  onZoneConfirmed: (args: { zoneCode: string }) => void;
};

type UploadStatus =
  | { kind: "idle" }
  | { kind: "uploading"; which: "reglement" | "graphique"; progress: number }
  | { kind: "uploaded" }
  | { kind: "validating" }
  | { kind: "rejected-not-plu" }
  | {
      kind: "rejected-commune-mismatch";
      detected: string | null;
    }
  | { kind: "rate-limited" }
  | { kind: "error"; message: string }
  | { kind: "confirm"; validation: PluUploadValidation; graphiqueUrl: string };

export function PluUploadModal(props: Props) {
  const [status, setStatus] = useState<UploadStatus>({ kind: "idle" });
  const [reglementFile, setReglementFile] = useState<File | null>(null);
  const [graphiqueFile, setGraphiqueFile] = useState<File | null>(null);
  const [selectedZone, setSelectedZone] = useState<string>("");
  const reglementInputRef = useRef<HTMLInputElement>(null);
  const graphiqueInputRef = useRef<HTMLInputElement>(null);

  // Diagnostic — we hit a "modal silently reset to upload step after ~2 min"
  // bug we couldn't repro deterministically. Logs let us catch the next
  // occurrence and trace whether it's a remount (parent's conditional
  // render flickered), an `open` prop flicker, or something else.
  // Remove once we've confirmed the root cause.
  useEffect(() => {
    console.log(
      "[PluUploadModal] mounted",
      { ficheId: props.ficheId, time: new Date().toISOString() },
    );
    return () => {
      console.log(
        "[PluUploadModal] unmounted",
        { ficheId: props.ficheId, time: new Date().toISOString() },
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset internal state on every open transition. Guarded against
  // accidental remount-induced resets : if state already has progress
  // (file picked, validation done) we skip the wipe — likely a parent
  // remount, not a fresh open.
  useEffect(() => {
    if (!props.open) return;
    const hasProgress =
      status.kind !== "idle" ||
      reglementFile !== null ||
      graphiqueFile !== null;
    console.log(
      "[PluUploadModal] open changed → true",
      { hasProgress, statusKind: status.kind },
    );
    if (hasProgress) {
      console.warn(
        "[PluUploadModal] preserved state across open transition (likely remount)",
      );
      return;
    }
    setStatus({ kind: "idle" });
    setReglementFile(null);
    setGraphiqueFile(null);
    setSelectedZone("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  if (!props.open) return null;

  const reglementPath = `users/${props.uid}/fiches/${props.ficheId}/pluUploads/reglement.pdf`;
  const graphiquePath = `users/${props.uid}/fiches/${props.ficheId}/pluUploads/graphique.pdf`;

  async function handleVerify() {
    if (!reglementFile || !graphiqueFile) return;
    if (
      reglementFile.size > MAX_PDF_BYTES ||
      graphiqueFile.size > MAX_PDF_BYTES
    ) {
      setStatus({
        kind: "error",
        message: `PDF trop volumineux (max ${Math.round(MAX_PDF_BYTES / 1_048_576)} Mo).`,
      });
      return;
    }
    if (
      reglementFile.type !== "application/pdf" ||
      graphiqueFile.type !== "application/pdf"
    ) {
      setStatus({
        kind: "error",
        message: "Les deux fichiers doivent être des PDF.",
      });
      return;
    }

    try {
      // Sequential uploads keep a clean progress signal — they're small enough
      // (< 80 MB each) that parallelizing wouldn't meaningfully help.
      setStatus({
        kind: "uploading",
        which: "reglement",
        progress: 0,
      });
      await uploadBytes(storageRef(storage, reglementPath), reglementFile, {
        contentType: "application/pdf",
      });

      setStatus({
        kind: "uploading",
        which: "graphique",
        progress: 0,
      });
      await uploadBytes(storageRef(storage, graphiquePath), graphiqueFile, {
        contentType: "application/pdf",
      });

      setStatus({ kind: "validating" });
      const validation = await validatePluUpload({
        ficheId: props.ficheId,
        expectedInsee: props.expectedInsee,
        expectedCommuneName: props.expectedCommuneName,
        lat: props.lat,
        lng: props.lng,
        parcelGeometryJson: JSON.stringify(props.parcel.geometry),
      });

      if (!validation.isPlu) {
        setStatus({ kind: "rejected-not-plu" });
        return;
      }
      if (!validation.communeMatches) {
        setStatus({
          kind: "rejected-commune-mismatch",
          detected: validation.detectedCommuneName,
        });
        return;
      }

      const graphiqueUrl = await getDownloadURL(storageRef(storage, graphiquePath));
      // Pre-fill select with the Vision prediction (or first zone if no
      // prediction). User must still actively confirm by clicking the CTA.
      setSelectedZone(
        validation.suggestedZone ??
          (validation.zones[0]?.code ?? ""),
      );
      setStatus({ kind: "confirm", validation, graphiqueUrl });
    } catch (e) {
      const msg = (e as { message?: string; code?: string }) ?? {};
      const code =
        (e as { details?: { code?: string } })?.details?.code ?? msg.code;
      if (code === "plu-upload-rate-limited") {
        setStatus({ kind: "rate-limited" });
        return;
      }
      setStatus({
        kind: "error",
        message: msg.message ?? "Erreur inattendue. Réessayez.",
      });
    }
  }

  function handleReset() {
    setStatus({ kind: "idle" });
  }

  // The confirm step needs to feel like a real workspace — switching to a
  // near-fullscreen modal so the agent has room to read the plan. Other
  // steps stay compact (file pickers, error states).
  const isConfirmStep = status.kind === "confirm";

  // Render the modal through a portal anchored to <body> — the Nouvelle
  // page wraps its content in a `.page-enter` div whose CSS animation
  // briefly applies a `transform`, which makes that div a *containing
  // block* for `position: fixed` descendants (CSS spec). Result : the
  // modal's `fixed inset-0` was anchoring on the 1160 px content column
  // instead of the viewport, leaving the sidebar and the canvas margins
  // un-dimmed. Portal escapes that hierarchy entirely.
  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="plu-upload-title"
      // Two layout modes :
      //   • compact (upload/error/etc.) — grid-centered, scrollable
      //     overlay. Modal content fits in 680 px.
      //   • confirm (PDF + ortho side-by-side) — flex-centered, both
      //     viewport axes constrained. Tried `grid place-items-center`
      //     here too but it lets the Card grow above 95vh in some
      //     viewports (Safari, ~1180 px laptops) — flex with explicit
      //     `items-center justify-center` is the reliable fix.
      className={`fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm ${
        isConfirmStep
          ? "flex items-center justify-center p-4"
          : "grid place-items-center p-4 overflow-y-auto"
      }`}
      onClick={props.onClose}
    >
      <Card
        className={`${
          isConfirmStep
            ? "w-[95vw] h-[95vh] max-w-none flex flex-col p-0 overflow-hidden"
            : "max-w-[680px] w-full max-h-[90vh] overflow-y-auto p-6"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`${
            isConfirmStep
              ? "shrink-0 flex items-center justify-between px-6 py-4 border-b border-ink-200"
              : "flex items-center justify-between"
          }`}
        >
          <Pill tone="ink" icon={<Ic.Bolt size={12} />}>
            Upload manuel du PLU
          </Pill>
          <button
            onClick={props.onClose}
            className="w-8 h-8 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500"
            aria-label="Fermer"
          >
            <Ic.Cross size={14} />
          </button>
        </div>

        <h3
          id="plu-upload-title"
          className={
            isConfirmStep
              ? "shrink-0 px-6 pt-4 pb-2 text-[20px] font-semibold tracking-tight"
              : "mt-3 text-[20px] font-semibold tracking-tight"
          }
        >
          {isConfirmStep
            ? "Confirmez la zone de la parcelle"
            : "Uploadez le règlement et le plan de zonage"}
        </h3>

        <Body
          status={status}
          reglementFile={reglementFile}
          graphiqueFile={graphiqueFile}
          setReglementFile={setReglementFile}
          setGraphiqueFile={setGraphiqueFile}
          reglementInputRef={reglementInputRef}
          graphiqueInputRef={graphiqueInputRef}
          selectedZone={selectedZone}
          setSelectedZone={setSelectedZone}
          expectedCommune={props.expectedCommuneName}
          parcel={props.parcel}
          ficheId={props.ficheId}
        />

        <Footer
          status={status}
          canVerify={Boolean(reglementFile && graphiqueFile)}
          selectedZone={selectedZone}
          onClose={props.onClose}
          onVerify={handleVerify}
          onReset={handleReset}
          onConfirm={() => {
            if (status.kind === "confirm" && selectedZone) {
              props.onZoneConfirmed({ zoneCode: selectedZone });
            }
          }}
        />
      </Card>
    </div>
  );

  return createPortal(overlay, document.body);
}

// ─── Body content (per status) ───────────────────────────────────────────
type BodyProps = {
  status: UploadStatus;
  reglementFile: File | null;
  graphiqueFile: File | null;
  setReglementFile: (f: File | null) => void;
  setGraphiqueFile: (f: File | null) => void;
  reglementInputRef: React.RefObject<HTMLInputElement>;
  graphiqueInputRef: React.RefObject<HTMLInputElement>;
  selectedZone: string;
  setSelectedZone: (z: string) => void;
  expectedCommune: string;
  parcel: CadastreParcel;
  ficheId: string;
};

function Body(p: BodyProps) {
  const { status } = p;

  if (status.kind === "uploading") {
    return (
      <div className="mt-5 space-y-3">
        <Spinner
          label={
            status.which === "reglement"
              ? "Envoi du règlement…"
              : "Envoi du plan graphique…"
          }
        />
      </div>
    );
  }

  if (status.kind === "validating") {
    return (
      <div className="mt-5 space-y-3">
        <Spinner label="Analyse IA en cours (vérification + localisation parcelle)…" />
        <p className="text-[12.5px] text-ink-600">
          Cette opération prend généralement 15-40 secondes.
        </p>
      </div>
    );
  }

  if (status.kind === "rejected-not-plu") {
    return (
      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-[13.5px] text-red-900">
        <strong>Le document ne ressemble pas à un PLU.</strong>
        <p className="mt-1.5 text-red-800">
          Vérifiez que vous avez bien uploadé le <em>règlement écrit du PLU</em> de
          la commune (et non un PLH, SCOT, PADD ou autre document
          d'urbanisme).
        </p>
      </div>
    );
  }

  if (status.kind === "rejected-commune-mismatch") {
    return (
      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-[13.5px] text-red-900">
        <strong>
          Mauvaise commune détectée :{" "}
          {status.detected ? `« ${status.detected} »` : "non identifiée"}.
        </strong>
        <p className="mt-1.5 text-red-800">
          La fiche concerne <strong>« {p.expectedCommune} »</strong>. Vérifiez
          que vous uploadez bien le PLU de la bonne commune.
        </p>
      </div>
    );
  }

  if (status.kind === "rate-limited") {
    return (
      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md text-[13.5px] text-amber-900">
        <strong>Trop de tentatives sur cette fiche.</strong>
        <p className="mt-1.5 text-amber-800">
          Pour éviter les abus, on limite à 5 vérifications par fiche.
          Contactez le support si vous pensez que c'est une erreur.
        </p>
      </div>
    );
  }

  if (status.kind === "error") {
    return (
      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-[13.5px] text-red-900">
        <strong>Une erreur est survenue.</strong>
        <p className="mt-1.5 font-mono text-[11.5px] text-red-700">
          {status.message}
        </p>
      </div>
    );
  }

  if (status.kind === "confirm") {
    return (
      <ConfirmStep
        validation={status.validation}
        graphiqueUrl={status.graphiqueUrl}
        selectedZone={p.selectedZone}
        setSelectedZone={p.setSelectedZone}
        parcel={p.parcel}
        ficheId={p.ficheId}
      />
    );
  }

  // idle / uploaded
  return (
    <UploadStep
      reglementFile={p.reglementFile}
      graphiqueFile={p.graphiqueFile}
      setReglementFile={p.setReglementFile}
      setGraphiqueFile={p.setGraphiqueFile}
      reglementInputRef={p.reglementInputRef}
      graphiqueInputRef={p.graphiqueInputRef}
    />
  );
}

// ─── Step 1 — file pickers ───────────────────────────────────────────────
type UploadStepProps = {
  reglementFile: File | null;
  graphiqueFile: File | null;
  setReglementFile: (f: File | null) => void;
  setGraphiqueFile: (f: File | null) => void;
  reglementInputRef: React.RefObject<HTMLInputElement>;
  graphiqueInputRef: React.RefObject<HTMLInputElement>;
};

function UploadStep(p: UploadStepProps) {
  return (
    <>
      <p className="mt-2 text-[13.5px] text-ink-700 leading-relaxed">
        La commune n'a pas publié son PLU sur le Géoportail de l'Urbanisme.
        Récupérez les deux PDF sur le site de la mairie ou de l'EPCI compétent
        et déposez-les ci-dessous. On vérifie automatiquement que c'est bien le
        bon document avant d'extraire.
      </p>

      <div className="mt-5 space-y-3">
        <FileInputRow
          label="Règlement écrit du PLU"
          hint="Document texte avec articles UA, UB, etc. (généralement 50–200 pages)"
          file={p.reglementFile}
          setFile={p.setReglementFile}
          inputRef={p.reglementInputRef}
        />
        <FileInputRow
          label="Plan graphique de zonage"
          hint="La carte avec les couleurs des zones (1 à 5 pages)"
          file={p.graphiqueFile}
          setFile={p.setGraphiqueFile}
          inputRef={p.graphiqueInputRef}
        />
      </div>

      <p className="mt-4 text-[12px] text-ink-500">
        PDF uniquement, 80 Mo max par fichier.
      </p>
    </>
  );
}

function FileInputRow({
  label,
  hint,
  file,
  setFile,
  inputRef,
}: {
  label: string;
  hint: string;
  file: File | null;
  setFile: (f: File | null) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div className="border border-ink-200 rounded-md p-3 hover:border-ink-300 transition">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-ink-900">{label}</div>
          <div className="text-[11.5px] text-ink-500">{hint}</div>
          {file && (
            <div className="mt-1 text-[12px] text-ink-700 truncate">
              ✓ {file.name}{" "}
              <span className="text-ink-500">
                ({(file.size / 1_048_576).toFixed(1)} Mo)
              </span>
            </div>
          )}
        </div>
        <Button
          variant={file ? "soft" : "outline"}
          size="sm"
          onClick={() => inputRef.current?.click()}
        >
          {file ? "Changer" : "Choisir"}
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setFile(f);
        }}
      />
    </div>
  );
}

// ─── Step 2 — confirmation (PDF left, ortho + zone select right) ─────────
//
// Layout : 2-col grid (lg) qui remplit la hauteur dispo de la modal.
//   • Col gauche (60 %) — plan graphique rendu via PDF.js. L'agent peut
//     **cliquer sur sa parcelle** pour déposer un pin → Claude Vision
//     identifie la zone à cet endroit précis (refinePluZoneFromPin).
//     Bouton « Ouvrir dans un nouvel onglet » pour le viewer PDF natif.
//   • Col droite (40 %, scrollable) — orthophoto de la parcelle (visual
//     anchor pour matcher au plan), bandeau de prédiction IA (init →
//     éventuellement raffinée par le pin), select zone, warnings, reasoning.
function ConfirmStep({
  validation,
  graphiqueUrl,
  selectedZone,
  setSelectedZone,
  parcel,
  ficheId,
}: {
  validation: PluUploadValidation;
  graphiqueUrl: string;
  selectedZone: string;
  setSelectedZone: (z: string) => void;
  parcel: CadastreParcel;
  ficheId: string;
}) {
  const sortedZones = useMemo(() => {
    const list: PluUploadZone[] = [...validation.zones];
    list.sort((a, b) => a.code.localeCompare(b.code, "fr", { numeric: true }));
    return list;
  }, [validation.zones]);

  // The pin refinement runs on top of the initial validation. We track
  // the refined prediction separately so we can show "auto-detected" vs
  // "refined by your pin" lineage to the agent.
  const [pinResult, setPinResult] = useState<RefinedPluZone | null>(null);
  const [pinning, setPinning] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  // Pending pin = user dropped a marker but hasn't asked us to analyse
  // it yet. Two-step "drop then confirm" UX prevents the cost-burn we
  // saw early on : every accidental click during a pan was triggering
  // a Claude Vision call, eating into the 10-pin/fiche cap fast.
  const [pendingPin, setPendingPin] = useState<PinPayload | null>(null);
  // Latest concurrent pin call — tagged so a fast user click during a
  // slow Vision call doesn't have its result overwritten by an earlier
  // (now-stale) response landing later.
  const pinTokenRef = useRef(0);

  const handlePin = (payload: PinPayload) => {
    // Drop-only — no backend call. The user explicitly hits "Analyser
    // ce point" once they're happy with the marker placement.
    setPendingPin(payload);
    setPinError(null);
    // Clear any previous pin's result — it referred to a different
    // position. The validation's auto-suggestion (visualised via
    // activePrediction fallback) takes over until the new pin is
    // analysed.
    setPinResult(null);
  };

  const handleAnalyzePin = async () => {
    if (!pendingPin) return;
    const payload = pendingPin;
    const myToken = ++pinTokenRef.current;
    setPinning(true);
    setPinError(null);
    try {
      const result = await refinePluZoneFromPin({
        ficheId,
        pageImageBase64: payload.imageBase64,
        knownZones: sortedZones.map((z) => z.code),
      });
      // If a newer pin already fired, drop this stale result.
      if (myToken !== pinTokenRef.current) return;
      setPinResult(result);
      if (result.suggestedZone) {
        setSelectedZone(result.suggestedZone);
      }
    } catch (e) {
      if (myToken !== pinTokenRef.current) return;
      const code = (e as { details?: { code?: string }; code?: string })
        ?.details?.code ??
        (e as { code?: string })?.code ??
        "unknown";
      const message =
        code === "plu-pin-rate-limited"
          ? "Trop de pins sur cette fiche (10 max). Sélectionnez la zone manuellement."
          : (e instanceof Error ? e.message : String(e));
      setPinError(message);
    } finally {
      if (myToken === pinTokenRef.current) setPinning(false);
    }
  };

  // Active prediction = pin refinement if available, else initial validation.
  const activePrediction = pinResult ?? {
    suggestedZone: validation.suggestedZone,
    confidence: validation.visionConfidence,
    reasoning: validation.visionReasoning ?? "",
  };
  const confidenceLabel = activePrediction.confidence
    ? confidenceMap[activePrediction.confidence]
    : null;
  const confidenceTone =
    activePrediction.confidence === "high"
      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : activePrediction.confidence === "medium"
        ? "bg-amber-50 border-amber-200 text-amber-900"
        : "bg-ink-50 border-ink-200 text-ink-700";

  return (
    <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-4 px-6 pb-4">
      {/* ── Left: PDF viewer with click-to-pin (8/12 ≈ 66 %) ─────────── */}
      <div className="lg:col-span-8 flex flex-col min-h-0">
        <div className="shrink-0 flex items-center justify-between mb-2">
          <div className="text-[12px] text-ink-600">
            Plan graphique — <strong>cliquez sur votre parcelle</strong> pour
            identifier précisément sa zone
          </div>
          <a
            href={graphiqueUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[12px] text-ink-700 hover:text-ink-900 underline"
          >
            <Ic.Arrow size={11} />
            Ouvrir dans un nouvel onglet
          </a>
        </div>
        <div className="flex-1 min-h-0 border border-ink-200 rounded-md overflow-hidden">
          <PdfPagePinViewer
            pdfUrl={graphiqueUrl}
            onPin={handlePin}
            busy={pinning}
            className="h-full"
          />
        </div>
      </div>

      {/* ── Right: ortho + prediction + select + reasoning (4/12) ─────── */}
      <div className="lg:col-span-4 flex flex-col min-h-0 overflow-y-auto pr-1 space-y-4">
        <div>
          <div className="text-[12px] text-ink-600 mb-2">
            Votre parcelle (pour repérage visuel)
          </div>
          <div className="aspect-[3/2] rounded-md overflow-hidden hairline">
            <SatelliteMap parcel={parcel} className="w-full h-full" />
          </div>
          <p className="mt-2 text-[11.5px] text-ink-500 leading-snug">
            Repérez la forme de la parcelle (contour lime) sur l'orthophoto,
            puis cliquez à l'endroit correspondant sur le plan graphique.
          </p>
        </div>

        <div
          className={`p-3 rounded-md border text-[13px] leading-relaxed ${confidenceTone}`}
        >
          <strong className="block text-[12px] uppercase tracking-wide font-semibold">
            {pinResult ? "Zone identifiée par votre pin" : "Document validé"}
          </strong>
          <p className="mt-1">
            {!pinResult && (
              <>
                PLU de{" "}
                <strong>« {validation.detectedCommuneName ?? "?"} »</strong>.{" "}
              </>
            )}
            {activePrediction.suggestedZone ? (
              <>
                {pinResult ? "Zone détectée : " : "L'IA suggère "}
                <strong>zone {activePrediction.suggestedZone}</strong>
                {confidenceLabel ? ` (confiance ${confidenceLabel})` : ""}.
                {!pinResult && " Vérifiez et corrigez si besoin."}
              </>
            ) : (
              <>
                {pinResult
                  ? "Pas de zone identifiable au point cliqué — réessayez ailleurs ou choisissez manuellement."
                  : "Pas de prédiction automatique — cliquez sur votre parcelle ou sélectionnez la zone manuellement."}
              </>
            )}
          </p>
          {pinning && (
            <p className="mt-2 text-[11.5px] flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
              Analyse IA en cours…
            </p>
          )}
        </div>

        {/* Two-step pin → analyse. The pin itself is just visual; the
            agent confirms with this CTA before we burn an API call.
            Capped at 10/fiche server-side, but explicit confirmation
            stops accidental clicks during pan from blowing through it. */}
        {pendingPin && !pinning && (
          <div className="p-3 bg-lime-50 border border-lime-300 rounded-md space-y-2">
            <p className="text-[12.5px] text-ink-800 leading-snug">
              <strong>Marker positionné.</strong> Cliquez sur «&nbsp;Analyser
              ce point&nbsp;» pour identifier la zone correspondante. Vous
              pouvez aussi déplacer le marker en cliquant ailleurs sur le
              plan.
            </p>
            <Button
              variant="dark"
              size="sm"
              className="w-full"
              onClick={() => {
                void handleAnalyzePin();
              }}
              icon={<Ic.Bolt size={13} />}
            >
              {pinResult ? "Ré-analyser ce point" : "Analyser ce point"}
            </Button>
          </div>
        )}

        {pinError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-[12.5px] text-red-900">
            ⚠ {pinError}
          </div>
        )}

        {validation.warnings.length > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-[12.5px] text-amber-900 space-y-1">
            {validation.warnings.map((w, i) => (
              <div key={i}>⚠ {w}</div>
            ))}
          </div>
        )}

        <div>
          <label
            htmlFor="zone-select"
            className="block text-[12px] text-ink-600 mb-1"
          >
            Zone de la parcelle
          </label>
          <select
            id="zone-select"
            value={selectedZone}
            onChange={(e) => setSelectedZone(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-ink-200 bg-paper text-[13.5px] focus:outline-none focus:ring-2 focus:ring-lime-300"
          >
            {sortedZones.length === 0 && (
              <option value="">Aucune zone détectée — entrez à la main</option>
            )}
            {sortedZones.map((z) => (
              <option key={z.code} value={z.code}>
                {z.code}
                {z.libelle ? ` — ${z.libelle}` : ""}
              </option>
            ))}
          </select>
          {sortedZones.length === 0 && (
            <input
              type="text"
              placeholder="Ex. UA, UB, 1AU, N…"
              value={selectedZone}
              onChange={(e) => setSelectedZone(e.target.value.trim())}
              className="mt-2 w-full h-10 px-3 rounded-md border border-ink-200 bg-paper text-[13.5px] focus:outline-none focus:ring-2 focus:ring-lime-300"
            />
          )}
        </div>

        {activePrediction.reasoning && (
          <details className="text-[12px] text-ink-500">
            <summary className="cursor-pointer hover:text-ink-700">
              Explication de la prédiction
            </summary>
            <p className="mt-1.5 p-3 bg-ink-50 rounded-md text-ink-700 leading-relaxed">
              {activePrediction.reasoning}
            </p>
          </details>
        )}
      </div>
    </div>
  );
}

const confidenceMap: Record<"high" | "medium" | "low", string> = {
  high: "élevée",
  medium: "moyenne",
  low: "faible",
};

// ─── Footer (CTAs vary per step) ─────────────────────────────────────────
type FooterProps = {
  status: UploadStatus;
  canVerify: boolean;
  selectedZone: string;
  onClose: () => void;
  onVerify: () => void;
  onReset: () => void;
  onConfirm: () => void;
};

function Footer(p: FooterProps) {
  const { status } = p;

  if (status.kind === "uploading" || status.kind === "validating") {
    return (
      <div className="mt-5 flex flex-col gap-2">
        <Button variant="soft" className="w-full" onClick={p.onClose} disabled>
          Annuler
        </Button>
      </div>
    );
  }

  if (
    status.kind === "rejected-not-plu" ||
    status.kind === "rejected-commune-mismatch" ||
    status.kind === "error"
  ) {
    return (
      <div className="mt-5 flex flex-col gap-2">
        <Button variant="dark" className="w-full" onClick={p.onReset}>
          Recommencer avec d'autres fichiers
        </Button>
        <Button variant="soft" className="w-full" onClick={p.onClose}>
          Fermer
        </Button>
      </div>
    );
  }

  if (status.kind === "rate-limited") {
    return (
      <div className="mt-5 flex flex-col gap-2">
        <Button variant="dark" className="w-full" onClick={p.onClose}>
          Fermer
        </Button>
      </div>
    );
  }

  if (status.kind === "confirm") {
    // Footer sticky en bas de la modal fullscreen — boutons côte-à-côte
    // au lieu de l'empilement vertical des autres steps (on a la place).
    return (
      <div className="shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-ink-200">
        <Button variant="soft" onClick={p.onClose}>
          Annuler
        </Button>
        <Button
          variant="primary"
          onClick={p.onConfirm}
          disabled={!p.selectedZone}
          icon={<Ic.Bolt size={14} />}
        >
          Confirmer la zone et lancer l'extraction
        </Button>
      </div>
    );
  }

  // idle / uploaded
  return (
    <div className="mt-5 flex flex-col gap-2">
      <Button
        variant="dark"
        className="w-full"
        onClick={p.onVerify}
        disabled={!p.canVerify}
        icon={<Ic.Bolt size={14} />}
      >
        Vérifier le document
      </Button>
      <Button variant="soft" className="w-full" onClick={p.onClose}>
        Annuler
      </Button>
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 text-[13px] text-ink-700">
      <span className="inline-block w-4 h-4 rounded-full border-2 border-ink-700 border-t-transparent animate-spin" />
      <span>{label}</span>
    </div>
  );
}
